import { BasicCard } from "@/components/BasicCard";
import { useTranslation } from "@vixi/localization";
import { RouteOptionsById, router, RouterIds, useNavigate } from "@vixi/router";
import {
  cn,
  Img,
  PageMainContent,
  useGoBack,
  FormHeading,
  FormDescription,
  useUiStore,
  Button,
} from "@vixi/ui";
import ChevronRightCircle from "@assets/icons/chevron-right-circle.svg?react";
import { ComponentPropsWithoutRef, forwardRef, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { paymentInfoQueryOptions } from "@vixi/api";
import { allCoursesCountQueryOptions } from "@/queries/courses/allCoursesCountQuery";
import { shouldShowUpgradePopup } from "@/utils/planCheck";

// Constants
const routeId = "/_app/create/" satisfies RouterIds;
const Route = router.routesById[routeId];

// Route
export const CreateCourseIndexPageOptions: RouteOptionsById<typeof routeId> = {
  component: RouteComponent,
  loader: async ({ context: { queryClient } }) => {
    // Pre-fetch payment info and all courses count for popup check
    // Use Promise.allSettled to prevent blocking if queries fail
    await Promise.allSettled([
      queryClient.ensureQueryData(paymentInfoQueryOptions()).catch(() => {}),
      queryClient.ensureQueryData(allCoursesCountQueryOptions()).catch(() => {}),
    ]);
  },
};

// Styled components
const KnowledgeSourceGridItem = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(
  (props, ref) => (
    <div
      ref={ref}
      className={cn(
        "col-span-full mx-auto flex w-66 flex-col items-center justify-center md:col-span-6 lg:col-span-full",
      )}
      {...props}
    />
  ),
);

function RouteComponent() {
  useGoBack(null);
  const { t, isRtl } = useTranslation();
  const navigate = useNavigate();
  const { showConfirmDialog } = useUiStore();

  // Get payment info and all courses count (regardless of status)
  const { data: paymentInfo, isSuccess: isPaymentInfoSuccess } = useQuery(paymentInfoQueryOptions());
  const { data: allCoursesCount, isSuccess: isAllCoursesSuccess } = useQuery(
    allCoursesCountQueryOptions(),
  );

  // Debug: Log data when loaded
  useEffect(() => {
    if (isPaymentInfoSuccess) {
      console.log("✅ [create/index] PaymentInfo loaded:", paymentInfo);
    }
    if (isAllCoursesSuccess) {
      console.log("✅ [create/index] AllCoursesCount loaded:", allCoursesCount);
    }
  }, [isPaymentInfoSuccess, isAllCoursesSuccess, paymentInfo, allCoursesCount]);

  const handleCardClick = (e: React.MouseEvent<HTMLAnchorElement>, targetPath: string) => {
    console.log("🔍 [create/index] Card clicked:", {
      targetPath,
      paymentInfo,
      allCoursesCount,
      typeOfAllCoursesCount: typeof allCoursesCount,
      isAllCoursesCountOne: allCoursesCount === 1,
    });
    
    const shouldShow = shouldShowUpgradePopup(paymentInfo, allCoursesCount);
    console.log("🔍 [create/index] shouldShowUpgradePopup result:", shouldShow);
    
    if (shouldShow) {
      e.preventDefault();
      showConfirmDialog({
        title: null,
        description: (
          <div className="flex flex-col items-center justify-center w-full h-full relative px-6 py-6">
            {/* Logo and Premium Text */}
            <div className="flex items-center mb-4">
              <Img src="/images/popuplogo.png" alt="Vixi Premium" className="w-[183px] h-[75px]" />
              <span className="text-[#B57CFF] text-[20px] font-bold">{t("Premium.Popup.PremiumLabel")}</span>
            </div>

            {/* Premium Popup Image */}
            <div className="flex items-center justify-center w-full mb-4">
              <Img
                src="/images/planpopup.png"
                alt="Vixi Premium"
                className="w-full max-h-[200px]"
                contain
              />
            </div>

            {/* Text */}
            <h1 className="text-[35px] font-bold text-center text-[#334D6E] mb-4">
              {t("Premium.Popup.GenerateMoreCourses")}
            </h1>

            {/* Try Vixi Premium Button */}
            <Button
              variant="contained"
              color="premium"
              size="large"
              className="px-8 py-3 rounded-full bg-gradient-to-r from-purple-400 to-purple-600 hover:from-purple-500 hover:to-purple-700 text-white font-medium"
              startIcon={<ChevronRightCircle className="w-5 h-5" />}
              onClick={() => {
                useUiStore.getState().hideConfirmDialog();
                navigate({ to: "/premium/plans" });
              }}
            >
              {t("Premium.Popup.TryPremium")}
            </Button>
          </div>
        ),
        dialogActions: null,
      });
    }
  };

  return (
    <PageMainContent scrollable ps="medium" psY="medium">
      <div className="my-auto flex max-h-200 flex-1 flex-col">
        {/* Header(s) */}
        <div className="bg-white rounded flex items-center justify-between">
          <FormHeading heading="CreateCourse.SelectKnowledgeSource">
            <FormDescription>{t("CreateCourse.OnlyOneSource")}</FormDescription>
          </FormHeading>
          <div className="qr flex items-center gap-2.5 me-7.5">
            <span className="text-foreground-secondary text-sm leading-snug">
              {t("Dashboard.ScanCodeLine1")}
              <br />
              {t("Dashboard.ScanCodeLine2")}
              <br />
              {t("Dashboard.ScanCodeLine3")}
            </span>
            <Img src="/images/logos/qr-code.png" />
          </div>
        </div>
        {/* Knowledge source cards */}
        <div
          className={cn(
            "mt-15 grid max-w-132 flex-1 grid-cols-12 content-start items-start lg:max-w-none lg:content-center lg:items-stretch ",
          )}
        >
          <div className="col-span-full lg:col-span-5">
            <div className="grid-cold-12 grid content-start justify-center gap-2.5  lg:content-between lg:gap-9">
              <KnowledgeSourceGridItem>
                <BasicCard
                  usage="create-course"
                  color="paper"
                  title={t("CreateCourse.Ebooks")}
                  description={t("CreateCourse.SelectEbooks")}
                  icon="/images/course-source/e-book.png"
                  to="/create/ebook"
                  onClick={(e) => handleCardClick(e, "/create/ebook")}
                />
              </KnowledgeSourceGridItem>

              <KnowledgeSourceGridItem>
                <BasicCard
                  usage="create-course"
                  color="paper"
                  title={t("CreateCourse.LinkedInContent")}
                  description={t("CreateCourse.SelectLinkedInContent")}
                  icon="/images/course-source/linkedin.png"
                  to="/create/linkedin"
                  onClick={(e) => handleCardClick(e, "/create/linkedin")}
                />
              </KnowledgeSourceGridItem>

          {/*    <KnowledgeSourceGridItem>
                <BasicCard
                  usage="create-course"
                  color="paper"
                  title={t("CreateCourse.PresentationSlides")}
                  description={t("CreateCourse.SelectPresentationSlides")}
                  icon="/images/course-source/presentation-slides.png"
                />
              </KnowledgeSourceGridItem>
          */} 
              <KnowledgeSourceGridItem className="relative">
                <BasicCard
                  usage="create-course"
                  color="paper"
                  title={t("CreateCourse.PodcastEpisodes")}
                  description={t("CreateCourse.SelectPodcastEpisodes")}
                  icon="/images/course-source/podcast-episodes.png"
                  onClick={(e) => handleCardClick(e, "")}
                />
                <img src="/images/soon.svg" alt="Podcast Episodes" className=" absolute bottom-[-10px] right-[-10px]" />
              </KnowledgeSourceGridItem>
            </div>
          </div>

          {/* Turn-into */}
          <div className="col-span-full lg:col-span-2">
            <div className="mt-2.5 mb-7.5 flex items-center justify-center pt-12.5 lg:mt-0 lg:mb-0 lg:h-full lg:flex-col lg:pt-0">
              <div className="relative">
                <Img
                  className="-scale-x-1.2 scale-y-1.2 rotate-7.05 absolute bottom-3/10 w-37.25 -translate-x-1/20 rtl:translate-x-28/100 rtl:scale-1"
                  src="/images/icons/swirly.png"
                />
                <h3 className="font-medium whitespace-nowrap">{t("CreateCourse.TurnItInto")}</h3>
              </div>
            </div>
          </div>

          {/* Example screens */}
          <div className="col-span-full lg:col-span-5">
            <div className="flex h-full flex-col items-center justify-center pb-10">
              <Img contain src="/images/screens/all-screens.png" className="h-99 w-91.25" />
              <h3 className="text-primary">{t("CreateCourse.GamifiedCourse")}</h3>
            </div>
          </div>
        </div>
      </div>
    </PageMainContent>
  );
}
